import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers } from '../../api';

/**
 * Format timestamp into compact relative time.
 */
const formatRelativeTime = (timestamp, isOnline) => {
  if (!timestamp) return isOnline ? 'Just now' : '—';
  const date = new Date(timestamp);
  const time = date.getTime();
  if (isNaN(time)) return isOnline ? 'Just now' : '—';

  const diffMs = Date.now() - time;
  if (diffMs < 45000) return 'Just now';
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMon = Math.floor(diffDay / 30.4);
  if (diffMon < 12) return `${Math.max(1, diffMon)}mo ago`;
  const diffYr = Math.floor(diffDay / 365);
  return `${Math.max(1, diffYr)}y ago`;
};

/**
 * Get color-coded avatar initials.
 */
const getAvatarStyle = (name = '') => {
  const clean = name.trim() || 'U';
  let hash = 0;
  for (let i = 0; i < clean.length; i++) {
    hash = clean.charCodeAt(i) + ((hash << 5) - hash);
  }
  const palettes = [
    { bg: 'bg-blue-100 text-blue-700 border-blue-200' },
    { bg: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    { bg: 'bg-violet-100 text-violet-700 border-violet-200' },
    { bg: 'bg-sky-100 text-sky-700 border-sky-200' },
    { bg: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { bg: 'bg-teal-100 text-teal-700 border-teal-200' },
    { bg: 'bg-amber-100 text-amber-700 border-amber-200' }
  ];
  const index = Math.abs(hash) % palettes.length;
  const initials = clean
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
  return { className: palettes[index].bg, initials: initials || 'U' };
};

export default function UserManagement() {
  const navigate = useNavigate();

  /* ================= ACCESS CONTROL =================
     Only isAdmin: true users can access it.
     Admin users must have dealer: null.
  ====================================================== */
  const isAdmin = sessionStorage.getItem('isAdmin') === 'true';
  const isDealer = sessionStorage.getItem('isDealer') === 'true';
  const dealerRaw = sessionStorage.getItem('dealer');
  const hasDealer =
    isDealer ||
    Boolean(dealerRaw && dealerRaw !== 'null' && dealerRaw !== 'undefined' && dealerRaw !== '');
  const canAccess = isAdmin && !hasDealer;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedDealer, setSelectedDealer] = useState('All Dealers');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('role');
  const [sortAsc, setSortAsc] = useState(false);
  const [activeUserDetail, setActiveUserDetail] = useState(null);

  // Redirect if unauthorized
  useEffect(() => {
    if (!canAccess) {
      navigate('/home/list', { replace: true });
    }
  }, [canAccess, navigate]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getUsers();
      if (res && res.users && Array.isArray(res.users)) {
        setUsers(res.users);
      } else if (res && res.error) {
        setError(res.message || 'Failed to load users');
      } else {
        setError('Invalid response received from server');
      }
    } catch (err) {
      console.error('[UserManagement] fetch error:', err);
      setError('Unable to load user management records.');
    } finally {
      setLoading(false);
    }
  }, [canAccess]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Unique dealers for filter dropdown
  const uniqueDealers = useMemo(() => {
    const set = new Set();
    users.forEach((u) => {
      if (u.dealer && typeof u.dealer === 'string' && u.dealer.trim()) {
        set.add(u.dealer.trim());
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [users]);

  // KPI Metrics
  const metrics = useMemo(() => {
    const total = users.length;
    const admins = users.filter((u) => u.isAdmin).length;
    const withDevices = users.filter((u) => u.deviceCount > 0).length;
    const totalDevices = users.reduce((acc, u) => acc + (u.deviceCount || 0), 0);
    const activeDevices = users.reduce((acc, u) => acc + (u.activeDeviceCount || 0), 0);
    const withDealer = users.filter((u) => Boolean(u.dealer)).length;
    return { total, admins, withDevices, totalDevices, activeDevices, withDealer };
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      // Role / Segment tab filter
      if (statusFilter === 'Admins' && !u.isAdmin) return false;
      if (statusFilter === 'With Devices' && u.deviceCount === 0) return false;
      if (statusFilter === 'With Dealer' && (!u.dealer || u.isAdmin)) return false;
      if (statusFilter === 'Without Dealer' && u.dealer) return false;

      // Dealer dropdown filter
      if (selectedDealer !== 'All Dealers') {
        if (u.dealer !== selectedDealer) return false;
      }

      // Search term
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const name = (u.name || '').toLowerCase();
        const mobile = (u.mobile || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const city = (u.city || '').toLowerCase();
        const state = (u.state || '').toLowerCase();
        const dealer = (u.dealer || '').toLowerCase();
        const machSerials = (u.devices || []).map((d) => (d.machSerial || '').toLowerCase()).join(' ');

        const matches =
          name.includes(term) ||
          mobile.includes(term) ||
          email.includes(term) ||
          city.includes(term) ||
          state.includes(term) ||
          dealer.includes(term) ||
          machSerials.includes(term);

        if (!matches) return false;
      }

      return true;
    });
  }, [users, statusFilter, selectedDealer, searchTerm]);

  // Sorted users
  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];
    list.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          break;
        case 'role':
          valA = a.isAdmin ? 1 : 0;
          valB = b.isAdmin ? 1 : 0;
          break;
        case 'dealer':
          valA = (a.dealer || '').toLowerCase();
          valB = (b.dealer || '').toLowerCase();
          break;
        case 'devices':
          valA = a.deviceCount || 0;
          valB = b.deviceCount || 0;
          break;
        case 'city':
          valA = (a.city || '').toLowerCase();
          valB = (b.city || '').toLowerCase();
          break;
        default:
          valA = a.isAdmin ? 1 : 0;
          valB = b.isAdmin ? 1 : 0;
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredUsers, sortField, sortAsc]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedUsers.slice(start, start + pageSize);
  }, [sortedUsers, currentPage, pageSize]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="bg-white border border-rose-200 rounded-xl p-8 max-w-md text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4 text-xl">
            <i className="fa-solid fa-lock" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-2">Admin Access Required</h2>
          <p className="text-sm text-slate-600 mb-6">
            The User Management directory is strictly restricted to administrator users without a dealer assignment.
          </p>
          <button
            onClick={() => navigate('/home/list')}
            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-lg transition-colors shadow-sm"
          >
            Return to Devices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-5 pb-20">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 sm:p-5 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600">
              <i className="fa-solid fa-users text-sm" />
            </div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
              User Management
            </h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
              Admin Only
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Manage system users, monitor connected IoT equipment, and view dealer affiliations.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 transition-colors disabled:opacity-50"
            title="Refresh User Data"
          >
            <i className={`fa-solid fa-rotate ${loading ? 'fa-spin text-blue-600' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="flex w-full gap-3.5">
        <div className="w-full bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-blue-300 transition-all">
          <div className="flex items-center justify-between text-slate-500 mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Users</span>
            <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-xs">
              <i className="fa-solid fa-users" />
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-900 tracking-tight">
            {loading ? '—' : metrics.total}
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
            <span>Registered accounts</span>
          </div>
        </div>

        <div className="w-full bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-indigo-300 transition-all">
          <div className="flex items-center justify-between text-slate-500 mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Admins</span>
            <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center text-xs">
              <i className="fa-solid fa-shield-halved" />
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-900 tracking-tight">
            {loading ? '—' : metrics.admins}
          </div>
          <div className="text-[11px] text-purple-600 mt-1 font-medium">
            dealer: null enforced
          </div>
        </div>

        <div className="w-full bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-emerald-300 transition-all">
          <div className="flex items-center justify-between text-slate-500 mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Device Owners</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center text-xs">
              <i className="fa-solid fa-microchip" />
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-900 tracking-tight">
            {loading ? '—' : metrics.withDevices}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            {loading ? '—' : `${Math.round((metrics.withDevices / (metrics.total || 1)) * 100)}% of user base`}
          </div>
        </div>

        <div className="w-full bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-sky-300 transition-all">
          <div className="flex items-center justify-between text-slate-500 mb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fleet Connected</span>
            <div className="w-7 h-7 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center text-xs">
              <i className="fa-solid fa-desktop" />
            </div>
          </div>
          <div className="text-2xl font-bold text-slate-900 tracking-tight">
            {loading ? '—' : metrics.totalDevices}
          </div>
          <div className="text-[11px] text-emerald-600 mt-1 font-medium flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>{metrics.activeDevices} devices online</span>
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        {/* Filter Controls Bar */}
        <div className="p-3.5 sm:p-4 border-b border-slate-200 bg-slate-50/70 space-y-3">
          {/* Top Row: Filter Tabs & Reset Action */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Segment Filter Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar py-0.5">
              {[
                { label: 'All Users', key: 'All', count: users.length, icon: 'fa-users' },
                { label: 'Admins', key: 'Admins', count: metrics.admins, icon: 'fa-shield-halved' },
                { label: 'With Devices', key: 'With Devices', count: metrics.withDevices, icon: 'fa-desktop' },
                { label: 'With Dealer', key: 'With Dealer', count: metrics.withDealer, icon: 'fa-store' },
                { label: 'Without Dealer', key: 'Without Dealer', count: users.length - metrics.withDealer, icon: 'fa-user' }
              ].map((tab) => {
                const active = statusFilter === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setStatusFilter(tab.key);
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                      active
                        ? 'bg-blue-600 text-white shadow-sm ring-1 ring-blue-600'
                        : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200'
                    }`}
                  >
                    <i className={`fa-solid ${tab.icon} text-[10px] ${active ? 'text-white' : 'text-slate-400'}`} />
                    <span>{tab.label}</span>
                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                        active ? 'bg-blue-700/90 text-white' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Quick Reset Filters Button */}
            {(searchTerm || statusFilter !== 'All' || selectedDealer !== 'All Dealers') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('All');
                  setSelectedDealer('All Dealers');
                  setCurrentPage(1);
                }}
                className="inline-flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700 font-medium px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-colors shrink-0"
              >
                <i className="fa-solid fa-rotate-left text-[10px]" />
                <span>Reset Filters</span>
              </button>
            )}
          </div>

          {/* Bottom Row: Spacious Search Input, Dealer Dropdown, and Page Size */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-2 border-t border-slate-200/70">
            {/* Prominent Search Bar (Spacious, min-w-[260px], never shrinks) */}
            <div className="relative flex-1 min-w-[260px] sm:max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search user, mobile, email, dealer, serial..."
                className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-8 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <i className="fa-solid fa-magnifying-glass text-xs" />
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600"
                  title="Clear search"
                >
                  <i className="fa-solid fa-circle-xmark text-xs" />
                </button>
              )}
            </div>

            {/* Right Controls: Dealer Filter, Page Size, Results Count */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 shrink-0">
              {/* Dealer Dropdown */}
              <div className="relative flex items-center">
                <i className="fa-solid fa-store absolute left-2.5 text-slate-400 text-[11px] pointer-events-none" />
                <select
                  value={selectedDealer}
                  onChange={(e) => {
                    setSelectedDealer(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-slate-200 bg-white text-slate-700 text-xs rounded-lg pl-7 pr-6 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm min-w-[190px] max-w-[230px] truncate cursor-pointer"
                >
                  <option value="All Dealers">All Dealers ({uniqueDealers.length})</option>
                  {uniqueDealers.map((dealer) => (
                    <option key={dealer} value={dealer}>
                      {dealer}
                    </option>
                  ))}
                </select>
              </div>

              {/* Page Size Selector */}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-slate-200 bg-white text-slate-700 text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm cursor-pointer shrink-0"
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>

              {/* Result Count Badge */}
              <div className="hidden md:flex items-center text-slate-600 text-xs px-2.5 py-2 bg-slate-100 rounded-lg border border-slate-200 font-mono shrink-0">
                <span className="font-semibold text-slate-800">{filteredUsers.length}</span>
                <span className="text-slate-400 ml-1">users</span>
              </div>
            </div>
          </div>
        </div>

        {/* Table View */}
        <div className="overflow-x-auto flex-1 custom-scrollbar">
          <table className="w-full text-left text-xs border-collapse border border-slate-200">
            <thead>
              <tr className="bg-slate-50/90 text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
                <th
                  onClick={() => handleSort('name')}
                  className="py-3 px-4 border border-slate-200 cursor-pointer select-none hover:text-blue-600 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>User / Name</span>
                    {sortField === 'name' && (
                      <i className={`fa-solid ${sortAsc ? 'fa-arrow-up' : 'fa-arrow-down'} text-[10px] text-blue-600`} />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 border border-slate-200">Contact Info</th>
                <th
                  onClick={() => handleSort('role')}
                  className="py-3 px-4 border border-slate-200 cursor-pointer select-none hover:text-blue-600 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Role</span>
                    {sortField === 'role' && (
                      <i className={`fa-solid ${sortAsc ? 'fa-arrow-up' : 'fa-arrow-down'} text-[10px] text-blue-600`} />
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('dealer')}
                  className="py-3 px-4 border border-slate-200 cursor-pointer select-none hover:text-blue-600 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Dealer</span>
                    {sortField === 'dealer' && (
                      <i className={`fa-solid ${sortAsc ? 'fa-arrow-up' : 'fa-arrow-down'} text-[10px] text-blue-600`} />
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('devices')}
                  className="py-3 px-4 border border-slate-200 cursor-pointer select-none hover:text-blue-600 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Connected Devices</span>
                    {sortField === 'devices' && (
                      <i className={`fa-solid ${sortAsc ? 'fa-arrow-up' : 'fa-arrow-down'} text-[10px] text-blue-600`} />
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('city')}
                  className="py-3 px-4 border border-slate-200 cursor-pointer select-none hover:text-blue-600 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Location</span>
                    {sortField === 'city' && (
                      <i className={`fa-solid ${sortAsc ? 'fa-arrow-up' : 'fa-arrow-down'} text-[10px] text-blue-600`} />
                    )}
                  </div>
                </th>
                <th className="py-3 px-4 border border-slate-200 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 border border-slate-200">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <i className="fa-solid fa-circle-notch fa-spin text-2xl text-blue-600" />
                      <span className="text-xs font-medium">Loading user management records...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-rose-600 bg-rose-50/50 border border-slate-200">
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <i className="fa-solid fa-triangle-exclamation text-lg" />
                      <span className="font-semibold text-xs">{error}</span>
                      <button
                        onClick={fetchUsers}
                        className="mt-2 text-xs font-semibold text-blue-600 hover:underline"
                      >
                        Try Again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 border border-slate-200">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                        <i className="fa-solid fa-user-slash text-base" />
                      </div>
                      <span className="font-medium text-xs text-slate-700">No users found</span>
                      <span className="text-[11px] text-slate-400">
                        Try clearing search terms or selecting another filter.
                      </span>
                      {(searchTerm || statusFilter !== 'All' || selectedDealer !== 'All Dealers') && (
                        <button
                          onClick={() => {
                            setSearchTerm('');
                            setStatusFilter('All');
                            setSelectedDealer('All Dealers');
                          }}
                          className="mt-1 px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-medium transition-colors"
                        >
                          Reset Filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => {
                  const avatar = getAvatarStyle(user.name);
                  const isUserAdmin = user.isAdmin;

                  return (
                    <tr
                      key={user._id}
                      onClick={() => setActiveUserDetail(user)}
                      className="hover:bg-blue-50/40 cursor-pointer transition-colors group"
                    >
                      {/* User / Name */}
                      <td className="py-3 px-4 border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs border shrink-0 ${avatar.className}`}
                          >
                            {avatar.initials}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate">
                              {user.name}
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono truncate">
                              ID: {String(user._id).slice(-8)}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Contact Info */}
                      <td className="py-3 px-4 border border-slate-200">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5 text-slate-700 font-mono text-xs">
                            <i className="fa-solid fa-phone text-[10px] text-slate-400" />
                            <span>{user.mobile}</span>
                          </div>
                          {user.email && user.email !== '—' && (
                            <div className="flex items-center gap-1.5 text-slate-500 text-[11px] truncate max-w-[200px]">
                              <i className="fa-solid fa-envelope text-[10px] text-slate-400" />
                              <span className="truncate">{user.email}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Role Badge */}
                      <td className="py-3 px-4 border border-slate-200">
                        {isUserAdmin ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                            <i className="fa-solid fa-shield-halved text-[9px]" />
                            <span>Admin</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                            <i className="fa-solid fa-user text-[9px]" />
                            <span>User</span>
                          </span>
                        )}
                      </td>

                      {/* Dealer Column: Admin must have dealer: null */}
                      <td className="py-3 px-4 border border-slate-200">
                        {isUserAdmin ? (
                          <span
                            className="inline-flex items-center gap-1 text-slate-400 text-xs italic"
                            title="Admin users have dealer: null"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                            <span>None (Admin)</span>
                          </span>
                        ) : user.dealer ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded bg-blue-50 text-blue-600 flex items-center justify-center text-[10px] shrink-0">
                              <i className="fa-solid fa-store" />
                            </div>
                            <span className="font-medium text-slate-800 text-xs max-w-[180px] truncate" title={user.dealer}>
                              {user.dealer}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>

                      {/* Connected Devices */}
                      <td className="py-3 px-4 border border-slate-200">
                        {user.deviceCount > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                              <i className="fa-solid fa-desktop text-[11px]" />
                              <span>{user.deviceCount} {user.deviceCount === 1 ? 'Device' : 'Devices'}</span>
                            </span>

                            {user.activeDeviceCount > 0 ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600" title={`${user.activeDeviceCount} active now`}>
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span>{user.activeDeviceCount} Online</span>
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400">Offline</span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-medium">
                            <span>0 Devices</span>
                          </span>
                        )}
                      </td>

                      {/* Location */}
                      <td className="py-3 px-4 border border-slate-200">
                        <div className="flex items-center gap-1.5 text-slate-600 text-xs">
                          <i className="fa-solid fa-location-dot text-[10px] text-slate-400 shrink-0" />
                          <span className="truncate max-w-[150px]" title={[user.city, user.state].filter((p) => p && p !== '—').join(', ')}>
                            {[user.city, user.state].filter((p) => p && p !== '—').join(', ') || '—'}
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 border border-slate-200 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveUserDetail(user);
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors"
                        >
                          <span>Details</span>
                          <i className="fa-solid fa-chevron-right text-[9px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        <div className="p-3.5 sm:p-4 border-t border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            Showing{' '}
            <span className="font-semibold text-slate-700">
              {sortedUsers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
            </span>{' '}
            to{' '}
            <span className="font-semibold text-slate-700">
              {Math.min(currentPage * pageSize, sortedUsers.length)}
            </span>{' '}
            of <span className="font-semibold text-slate-700">{sortedUsers.length}</span> users
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600"
              title="First Page"
            >
              <i className="fa-solid fa-angles-left text-xs" />
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600"
              title="Previous Page"
            >
              <i className="fa-solid fa-chevron-left text-xs" />
            </button>

            <span className="px-3 py-1 font-medium text-slate-700 bg-white border border-slate-200 rounded-lg text-xs">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600"
              title="Next Page"
            >
              <i className="fa-solid fa-chevron-right text-xs" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600"
              title="Last Page"
            >
              <i className="fa-solid fa-angles-right text-xs" />
            </button>
          </div>
        </div>
      </div>

      {/* User Details Slide-Over / Modal */}
      {activeUserDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-in">
          <div
            className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold text-sm border shrink-0 ${
                    getAvatarStyle(activeUserDetail.name).className
                  }`}
                >
                  {getAvatarStyle(activeUserDetail.name).initials}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-slate-900">{activeUserDetail.name}</h2>
                    {activeUserDetail.isAdmin ? (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200">
                        Admin
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                        User
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">
                    User ID: {String(activeUserDetail._id)}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setActiveUserDetail(null)}
                className="w-8 h-8 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
              >
                <i className="fa-solid fa-xmark text-sm" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 overflow-y-auto space-y-5 custom-scrollbar">
              {/* Profile Details Grid */}
              <div className="flex flex-col gap-3.5 bg-slate-50/60 p-4 rounded-xl border border-slate-200 text-xs">
                <div>
                  <span className="text-slate-400 font-medium block text-[11px]">Mobile Number</span>
                  <span className="font-semibold text-slate-800 font-mono text-xs">{activeUserDetail.mobile}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block text-[11px]">Email Address</span>
                  <span className="font-semibold text-slate-800 text-xs">{activeUserDetail.email || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block text-[11px]">Dealer Assignment</span>
                  <span className="font-semibold text-slate-800 text-xs">
                    {activeUserDetail.isAdmin ? 'None (Admin users have dealer: null)' : activeUserDetail.dealer || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block text-[11px]">Machine Type</span>
                  <span className="font-semibold text-slate-800 text-xs">{activeUserDetail.machinename || 'DUO IQ'}</span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-slate-400 font-medium block text-[11px]">Complete Address</span>
                  <span className="text-slate-700 text-xs">
                    {[activeUserDetail.addr, activeUserDetail.city, activeUserDetail.state, activeUserDetail.pincode]
                      .filter((p) => p && p !== '—')
                      .join(', ') || 'No street address provided'}
                  </span>
                </div>
              </div>

              {/* Connected Devices Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-microchip text-slate-500 text-sm" />
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Connected Devices ({activeUserDetail.deviceCount})
                    </h3>
                  </div>
                  {activeUserDetail.activeDeviceCount > 0 && (
                    <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      {activeUserDetail.activeDeviceCount} Active
                    </span>
                  )}
                </div>

                {activeUserDetail.devices && activeUserDetail.devices.length > 0 ? (
                  <div className="space-y-2">
                    {activeUserDetail.devices.map((dev, idx) => (
                      <div
                        key={dev.machSerial || idx}
                        className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between gap-3 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs ${
                              dev.isActive
                                ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}
                          >
                            <i className="fa-solid fa-desktop" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-slate-800 text-xs">
                                {dev.machSerial}
                              </span>
                              <span
                                className={`px-1.5 py-0.2 rounded text-[10px] font-semibold ${
                                  dev.isActive
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {dev.isActive ? 'ONLINE' : 'OFFLINE'}
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5">
                              Model: {dev.machinetype || activeUserDetail.machinename || 'DUO IQ'} · Last active:{' '}
                              {formatRelativeTime(dev.lastActive, dev.isActive)}
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            setActiveUserDetail(null);
                            navigate(`/home/details/${dev.machSerial}`);
                          }}
                          className="px-2.5 py-1 bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-700 rounded-lg text-xs font-medium transition-colors"
                        >
                          Telemetry
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-xs">
                    <i className="fa-solid fa-laptop-code text-lg mb-1 block" />
                    No connected devices are currently registered to this user account.
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end">
              <button
                onClick={() => setActiveUserDetail(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-semibold rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
